import { isAnyAnswer, type Condition, type CrossState } from './builderModel'
import type { Category } from './useBuilderData'

// selectionModel is the pure half of Cross two variables as the tree's ticks
// drive it. The tree's selection is the same one Codes uses
// (useVariableCatalog's `selection`): one condition per ticked variable, in
// tree order, whose codes are either the ticked answers or [ANY_ANSWER] for
// the whole variable. Here it is placed into cross slots; the rules those
// save are written by builderModel, unchanged.

// resolveAnswers turns one ticked variable into the answers it stands for:
// every answer for the whole variable ("any answer"), otherwise the ticked
// ones. `all` is every answer of the variable. The
// categories are loaded when not already known (Carbon varCodes, which reads
// no case data).
export const resolveAnswers = async (
  pick: Condition,
  known: Category[] | undefined,
  load: (variable: string) => Promise<Category[]>,
): Promise<{ answers: Category[], all: Category[] }> => {
  const all = known ?? await load(pick.variable)
  const answers = isAnyAnswer(pick.codes)
    ? all
    : pick.codes.map(code => all.find(c => c.code === code) || { code, label: code })
  return { answers, all }
}

// How a selection fits Cross two variables: one question fills a slot, two
// fill both, more do not fit.
export type CrossFit = 'none' | 'one' | 'two' | 'tooMany'

export const crossSelectionFit = (selection: Condition[]): CrossFit => {
  if (selection.length === 0) return 'none'
  if (selection.length === 1) return 'one'
  if (selection.length === 2) return 'two'
  return 'tooMany'
}

export type CrossSlot = 'rows' | 'cols'

export const emptyCross = (): CrossState => ({ rows: '', cols: '', rowCodes: [], rowLabels: {}, colGroups: [] })

// setCrossSlot fills a slot with a question and the answers it uses (all of
// them, or the ticked ones), or clears it with null. The first question's
// answers are the grid's rows; the second's are its columns, one each (they
// can be merged in the grid afterwards).
export const setCrossSlot = (
  cross: CrossState | null,
  slot: CrossSlot,
  pick: { variable: string, answers: Category[] } | null,
): CrossState => {
  const base = cross || emptyCross()
  if (slot === 'rows') {
    return {
      ...base,
      rows: pick?.variable || '',
      rowCodes: pick ? pick.answers.map(a => a.code) : [],
      rowLabels: pick ? Object.fromEntries(pick.answers.map(a => [a.code, a.label])) : {},
    }
  }
  return {
    ...base,
    cols: pick?.variable || '',
    colGroups: pick ? pick.answers.map(a => ({ label: a.label, codes: [a.code] })) : [],
  }
}

// swapCross swaps the two questions, keeping the answers each one uses.
// Merged columns become separate rows (rows cannot be merged).
export const swapCross = (cross: CrossState, labelOf: (variable: string, code: string) => string): CrossState => {
  const rowCodes = cross.colGroups.flatMap(g => g.codes)
  return {
    rows: cross.cols,
    cols: cross.rows,
    rowCodes,
    rowLabels: Object.fromEntries(rowCodes.map(c => [c, labelOf(cross.cols, c)])),
    colGroups: cross.rowCodes.map(c => ({ label: cross.rowLabels[c] || labelOf(cross.rows, c), codes: [c] })),
  }
}
